import { useState } from 'react';
import clsx from 'clsx';
import { CheckCircle2, ClipboardCopy, Download, Upload } from 'lucide-react';
import { ToolHeader } from '../components/ToolHeader';
import { downloadBlob } from '../../lib/blob';
import {
  FILE_SIZE_CAP_BYTES,
  dataUriToBlob,
  fileToDataUri,
  type DataUriResult,
} from '../lib/base64File';

type Tab = 'encode' | 'decode';

/**
 * `/tools/encode/base64-file` — file ↔ Data URI.
 *
 * Two tabs: encode (file → text) and decode (text → file). Each tab
 * keeps its own buffer so users can flip back and forth without
 * losing work.
 */
export default function Base64FileTool(): JSX.Element {
  const [tab, setTab] = useState<Tab>('encode');
  const [encoded, setEncoded] = useState<DataUriResult | null>(null);
  const [encodeError, setEncodeError] = useState<string>('');

  const [decodeText, setDecodeText] = useState<string>('');
  const [decodeError, setDecodeError] = useState<string>('');

  const onPickFile = async (files: FileList | null): Promise<void> => {
    if (!files || files.length === 0) return;
    const file = files[0];
    setEncodeError('');
    try {
      const r = await fileToDataUri(file);
      setEncoded(r);
    } catch (e) {
      setEncoded(null);
      setEncodeError(e instanceof Error ? e.message : String(e));
    }
  };

  const onDecodeDownload = (): void => {
    setDecodeError('');
    try {
      const r = dataUriToBlob(decodeText);
      const ext = mimeToExtension(r.mime);
      downloadBlob(r.blob, `decoded.${ext}`);
    } catch (e) {
      setDecodeError(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <div className="flex h-full min-h-0 flex-col gap-3 p-4">
      <ToolHeader
        toolId="base64-file"
        category="encode"
        description={`文件 ↔ Data URI 互转。上限 ${(FILE_SIZE_CAP_BYTES / 1024 / 1024).toFixed(0)} MB；解码端兼容裸 base64（无 data: 前缀）+ URL-safe (-/_) + 任意空白。`}
      />

      <div className="flex flex-wrap items-center gap-1">
        {(['encode', 'decode'] as Tab[]).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={clsx(
              'rounded-md border px-2 py-0.5 text-[11.5px] font-medium transition',
              tab === t
                ? 'border-accent bg-accent/10 text-accent'
                : 'border-line text-ink-muted hover:border-accent/60 hover:text-accent',
            )}
          >
            {t === 'encode' ? '文件 → Data URI' : 'Data URI → 文件'}
          </button>
        ))}
      </div>

      {tab === 'encode' ? (
        <EncodePane
          encoded={encoded}
          error={encodeError}
          onPick={onPickFile}
        />
      ) : (
        <DecodePane
          text={decodeText}
          onTextChange={setDecodeText}
          error={decodeError}
          onDownload={onDecodeDownload}
        />
      )}
    </div>
  );
}

function EncodePane({
  encoded,
  error,
  onPick,
}: {
  encoded: DataUriResult | null;
  error: string;
  onPick: (f: FileList | null) => Promise<void>;
}): JSX.Element {
  const [copied, setCopied] = useState(false);
  const onCopy = async (): Promise<void> => {
    if (!encoded) return;
    try {
      await navigator.clipboard.writeText(encoded.dataUri);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 900);
    } catch {
      /* ignore */
    }
  };

  return (
    <section className="card-pad flex min-h-0 flex-1 flex-col gap-2">
      <header className="flex items-center justify-between">
        <span className="text-[11px] uppercase tracking-wide text-ink-dim">
          选择文件
        </span>
        {encoded && (
          <span className="text-[11px] text-ink-dim">
            {encoded.mime} · {formatBytes(encoded.size)}
          </span>
        )}
      </header>

      <label className="flex cursor-pointer items-center justify-center gap-2 rounded-md border-2 border-dashed border-line bg-bg-soft p-6 text-[13px] text-ink-muted hover:border-accent/60 hover:text-accent">
        <Upload className="h-4 w-4" />
        <span>点击选择文件，或拖放进来</span>
        <input
          type="file"
          className="hidden"
          onChange={(e) => void onPick(e.currentTarget.files)}
        />
      </label>

      {error && (
        <p className="rounded-md border border-rose-500/30 bg-rose-500/10 px-3 py-1.5 text-[12px] text-rose-700 dark:text-rose-300">
          {error}
        </p>
      )}

      {encoded && (
        <>
          <div className="flex items-center justify-between">
            <span className="text-[11px] text-ink-dim">Data URI</span>
            <button
              type="button"
              onClick={onCopy}
              className={clsx(
                'inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[11.5px] font-medium transition',
                copied
                  ? 'border-emerald-500/50 bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300'
                  : 'border-line text-ink-muted hover:border-accent/60 hover:text-accent',
              )}
            >
              {copied ? (
                <CheckCircle2 className="h-3 w-3" />
              ) : (
                <ClipboardCopy className="h-3 w-3" />
              )}
              {copied ? '已复制' : '复制'}
            </button>
          </div>
          <textarea
            className="input min-h-0 flex-1 resize-none font-mono text-[11.5px] leading-snug"
            value={encoded.dataUri}
            readOnly
            spellCheck={false}
          />
        </>
      )}
    </section>
  );
}

function DecodePane({
  text,
  onTextChange,
  error,
  onDownload,
}: {
  text: string;
  onTextChange: (s: string) => void;
  error: string;
  onDownload: () => void;
}): JSX.Element {
  return (
    <section className="card-pad flex min-h-0 flex-1 flex-col gap-2">
      <header className="flex items-center justify-between">
        <span className="text-[11px] uppercase tracking-wide text-ink-dim">
          Data URI 或 base64
        </span>
        <button
          type="button"
          onClick={onDownload}
          disabled={!text.trim()}
          className="inline-flex items-center gap-1 rounded-md border border-line px-2 py-0.5 text-[11.5px] font-medium text-ink-muted hover:border-accent/60 hover:text-accent disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Download className="h-3 w-3" />
          解码并下载
        </button>
      </header>
      <textarea
        className="input min-h-0 flex-1 resize-none font-mono text-[11.5px] leading-snug"
        value={text}
        onChange={(e) => onTextChange(e.target.value)}
        placeholder={'data:image/png;base64,iVBORw0KGgo... 或 直接粘贴裸 base64'}
        spellCheck={false}
      />
      {error && (
        <p className="rounded-md border border-rose-500/30 bg-rose-500/10 px-3 py-1.5 text-[12px] text-rose-700 dark:text-rose-300">
          {error}
        </p>
      )}
    </section>
  );
}

function mimeToExtension(mime: string): string {
  const m = mime.toLowerCase();
  if (m.includes('png')) return 'png';
  if (m.includes('jpeg') || m.includes('jpg')) return 'jpg';
  if (m.includes('webp')) return 'webp';
  if (m.includes('gif')) return 'gif';
  if (m.includes('svg')) return 'svg';
  if (m.includes('pdf')) return 'pdf';
  if (m.includes('json')) return 'json';
  if (m.includes('xml')) return 'xml';
  if (m.includes('html')) return 'html';
  if (m.includes('text/plain')) return 'txt';
  return 'bin';
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}
